import { Collection } from 'firestorter';
import moment from 'moment';

import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

// How far back from a check-in day logs are read, matching the history the mobile app keeps loaded. Predictions only
// need the latest period start.
const DAY_LOG_HISTORY_MONTHS = 12;

/**
 * A client's menstrual cycle tracker settings, at `cycleHealth/{userId}`, written by the mobile app. Logged days live
 * in the `dayLogs` subcollection. Coach views read it only while the client's user doc has `isCycleTrackingActive`;
 * the rules also deny it to anyone but admins and the assigned coach, and to them once tracking is off.
 */
class CycleHealth extends BaseDocument {
  constructor(userId, opts) {
    super(`${Collections.CYCLE_HEALTH}/${userId}`, opts);
  }

  get settings() {
    return {
      periodLength: this.data.periodLength,
      cycleLength: this.data.cycleLength,
      lastPeriodStart: this.data.lastPeriodStart || undefined,
    };
  }

  /**
   * The client's logged days, keyed by date id (YYYY-MM-DD). Predictions anchor on the latest logged period start,
   * which can be well before the check-in week, so the logs from DAY_LOG_HISTORY_MONTHS before `windowEndId` are read.
   *
   * @param {string} userId The client.
   * @param {string} windowEndId The last day of the check-in window, as the client's local date (YYYY-MM-DD).
   * @returns {Promise<Object>} Day logs keyed by date id.
   */
  static async getDayLogs(userId, windowEndId) {
    const since = moment(windowEndId, 'YYYY-MM-DD')
      .subtract(DAY_LOG_HISTORY_MONTHS, 'months')
      .startOf('month')
      .format('YYYY-MM-DD');
    const dayLogsCollection = new Collection(
      `${Collections.CYCLE_HEALTH}/${userId}/${Collections.CYCLE_HEALTH_DAY_LOGS}`,
      { query: (ref) => ref.where('date', '>=', since) },
    );
    await dayLogsCollection.fetch();
    return Object.fromEntries(dayLogsCollection.docs.map((dayLog) => {
      const {
        date, isPeriod, isLastPeriodDay, symptoms, ovulated,
      } = dayLog.data;
      return [dayLog.id, {
        date: date || dayLog.id,
        isPeriod: !!isPeriod,
        isLastPeriodDay: !!isLastPeriodDay,
        symptoms: symptoms || [],
        ovulated: !!ovulated,
      }];
    }));
  }
}

export default CycleHealth;
